import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

interface Location {
  id: string;
  title?: string;
  address?: string;
  city?: string;
  latitude?: number | string;
  longitude?: number | string;
  price?: number;
  visitor?: number;
  finalLatitude?: number;
  finalLongitude?: number;
  isFallback?: boolean;
  images?: string[];
  type?: "apartment" | "office" | "rooftop" | "studio" | "other";
  rating?: number;
  isPopular?: boolean;
  isSuperHost?: boolean;
  availabilityStatus?: "available" | "busy" | "unavailable";
  description?: string;
  category?: string;
}

interface StableMapProps {
  locations: Location[];
  height?: string;
  className?: string;
  embedded?: boolean;
  onLocationSelect?: (location: Location) => void;
  onLocationHover?: (location: Location | null) => void;
  hoveredLocationId?: string | null;
  selectedLocationId?: string | null;
  onBoundsChange?: (bounds: any) => void;
  onFavoriteToggle?: (locationId: string) => void;
  favorites?: string[];
  showClustering?: boolean;
  showPhotos?: boolean;
}

const StableMap: React.FC<StableMapProps> = ({
  locations = [],
  height,
  className = "",
  embedded = false,
  onLocationSelect,
  onLocationHover,
  hoveredLocationId,
  selectedLocationId,
  onBoundsChange,
  onFavoriteToggle,
  favorites = [],
  showClustering = true,
  showPhotos = true,
}) => {
  // Use 100% height when embedded, otherwise default to 400px
  const mapHeight = height || (embedded ? "100%" : "400px");
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [mapHtml, setMapHtml] = useState<string>("");

  // Process locations once
  const processedLocations = React.useMemo(() => {
    const cityCoords: Record<string, { lat: number; lng: number }> = {
      // Main French cities
      paris: { lat: 48.8566, lng: 2.3522 },
      lyon: { lat: 45.764, lng: 4.8357 },
      marseille: { lat: 43.2965, lng: 5.3698 },
      toulouse: { lat: 43.6047, lng: 1.4442 },
      nice: { lat: 43.7102, lng: 7.262 },
      montpellier: { lat: 43.6108, lng: 3.8767 },
      bordeaux: { lat: 44.8378, lng: -0.5792 },
      lille: { lat: 50.6292, lng: 3.0573 },
      strasbourg: { lat: 48.5734, lng: 7.7521 },
      nantes: { lat: 47.2184, lng: -1.5536 },
      rennes: { lat: 48.1173, lng: -1.6778 },
      reims: { lat: 49.2583, lng: 4.0317 },
      "le havre": { lat: 49.4944, lng: 0.1079 },
      "saint-étienne": { lat: 45.4397, lng: 4.3872 },
      toulon: { lat: 43.1242, lng: 5.928 },
      grenoble: { lat: 45.1885, lng: 5.7245 },
      dijon: { lat: 47.322, lng: 5.0415 },
      angers: { lat: 47.4784, lng: -0.5632 },
      nîmes: { lat: 43.8367, lng: 4.3601 },
      villeurbanne: { lat: 45.7667, lng: 4.8833 },
      // Paris suburbs and specific areas
      montreuil: { lat: 48.8638, lng: 2.4469 },
      malakoff: { lat: 48.8158, lng: 2.2989 },
      montparnasse: { lat: 48.8428, lng: 2.321 },
      châtelet: { lat: 48.8606, lng: 2.3469 },
      marais: { lat: 48.8566, lng: 2.3522 },
      "saint-germain": { lat: 48.8532, lng: 2.3332 },
      république: { lat: 48.8676, lng: 2.3634 },
      bastille: { lat: 48.8531, lng: 2.3689 },
      belleville: { lat: 48.8719, lng: 2.3766 },
      pigalle: { lat: 48.8826, lng: 2.3376 },
      invalides: { lat: 48.8566, lng: 2.3122 },
      trocadéro: { lat: 48.8619, lng: 2.2876 },
      "roissy-en-france": { lat: 49.0034, lng: 2.5678 },
      roissy: { lat: 49.0034, lng: 2.5678 },
      volckerinckhove: { lat: 50.7167, lng: 2.4833 },
      // Other cities from data
      céret: { lat: 42.4833, lng: 2.75 },
      saverdun: { lat: 43.2333, lng: 1.5667 },
      orléans: { lat: 47.9029, lng: 1.9093 },
    };

    return locations
      .map((loc) => {
        let lat = parseFloat(loc.latitude?.toString() || "0");
        let lng = parseFloat(loc.longitude?.toString() || "0");
        let isFallback = false;

        // If invalid coordinates, use city fallback
        if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
          const cityName = (loc.city || "").toLowerCase();
          for (const [city, coords] of Object.entries(cityCoords)) {
            if (cityName.includes(city)) {
              lat = coords.lat;
              lng = coords.lng;
              isFallback = true;
              break;
            }
          }

          // Default to Paris if no match
          if (!lat || !lng) {
            lat = 48.8566;
            lng = 2.3522;
            isFallback = true;
          }
        }

        return {
          ...loc,
          finalLatitude: lat,
          finalLongitude: lng,
          isFallback,
        };
      })
      .filter((loc) => loc.finalLatitude && loc.finalLongitude);
  }, [locations]);

  // Debug: Log processed locations
  React.useEffect(() => {
    console.log("🗺️ Total locations received:", locations.length);
    console.log("🗺️ Valid locations processed:", processedLocations.length);
    console.log(
      "🗺️ Processed locations:",
      processedLocations.map((loc) => ({
        id: loc.id,
        title: loc.title,
        city: loc.city,
        coords: [loc.finalLatitude, loc.finalLongitude],
        isFallback: loc.isFallback,
      }))
    );
  }, [locations.length, processedLocations.length, processedLocations]);

  // Calculate center
  const center = React.useMemo(() => {
    if (processedLocations.length === 0) return { lat: 48.8566, lng: 2.3522 };

    const totalLat = processedLocations.reduce((sum, loc) => sum + loc.finalLatitude!, 0);
    const totalLng = processedLocations.reduce((sum, loc) => sum + loc.finalLongitude!, 0);

    return {
      lat: totalLat / processedLocations.length,
      lng: totalLng / processedLocations.length,
    };
  }, [processedLocations]);

  // Generate map HTML once
  useEffect(() => {
    const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Carte Interactive</title>
    <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" integrity="sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=" crossorigin=""/>
    <style>
        body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
        #map { height: 100vh; width: 100%; }
        .custom-marker {
            border: 3px solid white;
            border-radius: 50%;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
            font-size: 16px;
            box-shadow: 0 6px 16px rgba(0,0,0,0.4);
            cursor: pointer;
            transition: all 0.3s ease;
            background: #10b981;
        }
        .custom-marker:hover {
            transform: scale(1.3);
            z-index: 1000;
        }
        .leaflet-popup-content-wrapper {
            border-radius: 12px;
            box-shadow: 0 10px 25px rgba(0,0,0,0.15);
            border: none;
        }
        .leaflet-popup-content {
            margin: 0;
            padding: 16px;
            font-family: Arial, sans-serif;
        }
        .cluster-marker {
            background: transparent !important;
            border: none !important;
        }
        .cluster-marker:hover > div {
            transform: scale(1.1);
            transition: transform 0.2s ease;
            box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4) !important;
        }
        .cluster-marker > div {
            position: relative;
        }
        .cluster-marker > div::after {
            content: "Cliquer pour séparer";
            position: absolute;
            bottom: -25px;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(0,0,0,0.8);
            color: white;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 10px;
            white-space: nowrap;
            opacity: 0;
            transition: opacity 0.2s ease;
            pointer-events: none;
        }
        .cluster-marker:hover > div::after {
            opacity: 1;
        }
        .cluster-popup .leaflet-popup-content {
            padding: 12px;
        }
    </style>
</head>
<body>
    <div id="map"></div>
    <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js" integrity="sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=" crossorigin=""></script>
    <script>
        try {
            if (typeof L === 'undefined') {
                document.body.innerHTML = '<div style="padding: 20px; text-align: center; color: red;">❌ Erreur: Leaflet non chargé</div>';
                throw new Error('Leaflet not loaded');
            }

            var map = L.map('map').setView([${center.lat}, ${center.lng}], 11);

            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '© OpenStreetMap contributors'
            }).addTo(map);

            var locations = ${JSON.stringify(processedLocations)};
            var allMarkers = [];
            var expandedClusters = []; // Track which clusters are expanded

            // Group locations by proximity for clustering
            var clusters = [];
            var clusterRadius = 0.01; // ~1km radius for clustering

            console.log('🏗️ Starting clustering process with', locations.length, 'locations');

            locations.forEach(function(loc) {
                if (!loc.finalLatitude || !loc.finalLongitude) return;

                console.log('🔍 Processing location:', loc.title, 'at', [loc.finalLatitude, loc.finalLongitude]);

                // Find existing cluster within radius
                var foundCluster = null;
                for (var i = 0; i < clusters.length; i++) {
                    var cluster = clusters[i];
                    var distance = Math.sqrt(
                        Math.pow(cluster.centerLat - loc.finalLatitude, 2) +
                        Math.pow(cluster.centerLng - loc.finalLongitude, 2)
                    );

                    console.log('📏 Distance to cluster', i, ':', distance, '(threshold:', clusterRadius, ')');

                    if (distance <= clusterRadius) {
                        foundCluster = cluster;
                        console.log('✅ Adding to existing cluster', i);
                        break;
                    }
                }

                if (foundCluster) {
                    // Add to existing cluster
                    foundCluster.locations.push(loc);
                    // Recalculate center
                    foundCluster.centerLat = foundCluster.locations.reduce(function(sum, l) { return sum + l.finalLatitude; }, 0) / foundCluster.locations.length;
                    foundCluster.centerLng = foundCluster.locations.reduce(function(sum, l) { return sum + l.finalLongitude; }, 0) / foundCluster.locations.length;
                    foundCluster.cityName = foundCluster.locations[0].city || 'Zone';
                    console.log('📍 Cluster now has', foundCluster.locations.length, 'locations');
                } else {
                    // Create new cluster
                    var newClusterIndex = clusters.length;
                    clusters.push({
                        centerLat: loc.finalLatitude,
                        centerLng: loc.finalLongitude,
                        locations: [loc],
                        cityName: loc.city || 'Zone'
                    });
                    console.log('🆕 Created new cluster', newClusterIndex, 'for:', loc.title);
                }
            });

            console.log('🏁 Clustering complete. Total clusters:', clusters.length);
            clusters.forEach(function(cluster, index) {
                console.log('📊 Cluster', index, ':', cluster.locations.length, 'locations in', cluster.cityName);
            });

            // Function to create individual markers
            function createIndividualMarker(loc) {
                var marker = L.marker([loc.finalLatitude, loc.finalLongitude]);

                // Image section
                var imageHtml = '';
                if (loc.images && loc.images.length > 0) {
                    var imageUrl = loc.images[0];
                    if (!imageUrl.startsWith('http')) {
                        imageUrl = 'https://d2u1iivopux1rj.cloudfront.net/' + imageUrl;
                    }
                    imageHtml = '<div style="margin-bottom: 12px;">' +
                        '<img src="' + imageUrl + '" alt="' + (loc.title || 'Image') + '" style="width: 100%; height: 80px; object-fit: cover; border-radius: 6px;" onerror="this.style.display=\\'none\\'">' +
                    '</div>';
                }

                var popupContent = '<div style="min-width: 240px; max-width: 280px;">' +
                    imageHtml +
                    '<h3 style="margin: 0 0 8px 0; color: #333; font-size: 16px; font-weight: 600; line-height: 1.3;">' + (loc.title || 'Sans titre') + '</h3>' +
                    '<p style="margin: 0 0 6px 0; color: #666; font-size: 14px;">📍 ' + (loc.address || (loc.city || 'Ville inconnue')) + '</p>' +
                    (loc.visitor ? '<p style="margin: 0 0 6px 0; color: #666; font-size: 14px;">👥 ' + loc.visitor + ' personnes</p>' : '') +
                    (loc.price && loc.price > 0 ? '<p style="margin: 0 0 12px 0; color: #2563eb; font-weight: 600; font-size: 15px;">💰 ' + loc.price + '€/h</p>' : '<div style="margin-bottom: 12px;"></div>') +
                    '<button onclick="viewLocationDetails(\\''+loc.id+'\\')" style="width: 100%; background: #3b82f6; color: white; border: none; padding: 8px 16px; border-radius: 6px; font-size: 14px; font-weight: 500; cursor: pointer; transition: all 0.2s ease;" onmouseover="this.style.background=\\'#2563eb\\';" onmouseout="this.style.background=\\'#3b82f6\\';">👁️ Voir les détails</button>' +
                '</div>';

                marker.bindPopup(popupContent);
                marker.on('click', function() {
                    marker.openPopup();
                    if (window.parent && window.parent.postMessage) {
                        window.parent.postMessage({
                            type: 'locationSelect',
                            locationId: loc.id
                        }, '*');
                    }
                });

                return marker;
            }

            // Function to expand cluster
            function expandCluster(clusterIndex) {
                var cluster = clusters[clusterIndex];

                console.log('🔍 Expanding cluster', clusterIndex, 'with', cluster.locations.length, 'locations');
                console.log('📍 Cluster locations:', cluster.locations.map(function(loc) {
                    return { id: loc.id, title: loc.title, coords: [loc.finalLatitude, loc.finalLongitude] };
                }));

                // Check if already expanded
                if (expandedClusters.indexOf(clusterIndex) !== -1) {
                    console.log('⚠️ Cluster already expanded, skipping');
                    return;
                }

                expandedClusters.push(clusterIndex);

                // Remove cluster marker
                var clusterMarker = allMarkers.find(function(m) { return m._clusterId === clusterIndex; });
                if (clusterMarker) {
                    console.log('🗑️ Removing cluster marker');
                    map.removeLayer(clusterMarker);
                    allMarkers = allMarkers.filter(function(m) { return m !== clusterMarker; });
                }

                // Add individual markers with animation
                console.log('➕ Adding', cluster.locations.length, 'individual markers');
                cluster.locations.forEach(function(loc, index) {
                    setTimeout(function() {
                        console.log('📌 Creating marker', index + 1, 'for location:', loc.title);

                        // Create slightly offset coordinates for markers at same location
                        var lat = loc.finalLatitude;
                        var lng = loc.finalLongitude;

                        // If multiple locations in cluster, spread them slightly
                        if (cluster.locations.length > 1) {
                            var offset = 0.0002; // Small offset for visibility
                            var angle = (index * 360) / cluster.locations.length; // Distribute in circle
                            lat += offset * Math.cos(angle * Math.PI / 180);
                            lng += offset * Math.sin(angle * Math.PI / 180);
                        }

                        var marker = L.marker([lat, lng]);

                        // Image section
                        var imageHtml = '';
                        if (loc.images && loc.images.length > 0) {
                            var imageUrl = loc.images[0];
                            if (!imageUrl.startsWith('http')) {
                                imageUrl = 'https://d2u1iivopux1rj.cloudfront.net/' + imageUrl;
                            }
                            imageHtml = '<div style="margin-bottom: 12px;">' +
                                '<img src="' + imageUrl + '" alt="' + (loc.title || 'Image') + '" style="width: 100%; height: 80px; object-fit: cover; border-radius: 6px;" onerror="this.style.display=\\'none\\'">' +
                            '</div>';
                        }

                        var popupContent = '<div style="min-width: 240px; max-width: 280px;">' +
                            imageHtml +
                            '<h3 style="margin: 0 0 8px 0; color: #333; font-size: 16px; font-weight: 600; line-height: 1.3;">' + (loc.title || 'Sans titre') + '</h3>' +
                            '<p style="margin: 0 0 6px 0; color: #666; font-size: 14px;">📍 ' + (loc.address || (loc.city || 'Ville inconnue')) + '</p>' +
                            (loc.visitor ? '<p style="margin: 0 0 6px 0; color: #666; font-size: 14px;">👥 ' + loc.visitor + ' personnes</p>' : '') +
                            (loc.price && loc.price > 0 ? '<p style="margin: 0 0 12px 0; color: #2563eb; font-weight: 600; font-size: 15px;">💰 ' + loc.price + '€/h</p>' : '<div style="margin-bottom: 12px;"></div>') +
                            '<button onclick="viewLocationDetails(\\''+loc.id+'\\')" style="width: 100%; background: #3b82f6; color: white; border: none; padding: 8px 16px; border-radius: 6px; font-size: 14px; font-weight: 500; cursor: pointer; transition: all 0.2s ease;" onmouseover="this.style.background=\\'#2563eb\\';" onmouseout="this.style.background=\\'#3b82f6\\';">👁️ Voir les détails</button>' +
                        '</div>';

                        marker.bindPopup(popupContent);
                        marker.on('click', function() {
                            marker.openPopup();
                            if (window.parent && window.parent.postMessage) {
                                window.parent.postMessage({
                                    type: 'locationSelect',
                                    locationId: loc.id
                                }, '*');
                            }
                        });

                        marker._clusterIndex = clusterIndex;
                        marker._originalLocation = loc;
                        allMarkers.push(marker);
                        marker.addTo(map);
                        console.log('✅ Marker added to map for:', loc.title, 'at coordinates:', [lat, lng]);
                    }, index * 100); // Staggered animation
                });

                // Zoom to cluster bounds with delay for animation
                setTimeout(function() {
                    if (cluster.locations.length > 1) {
                        // For small clusters, zoom closer
                        var zoomLevel = cluster.locations.length === 2 ? 16 : 15;
                        map.setView([cluster.centerLat, cluster.centerLng], zoomLevel);
                        console.log('🔍 Zooming to cluster center at level', zoomLevel);
                    }
                }, cluster.locations.length * 100);
            }

            // Function to collapse cluster (re-group)
            function collapseCluster(clusterIndex) {
                var cluster = clusters[clusterIndex];

                // Remove from expanded list
                var expandedIndex = expandedClusters.indexOf(clusterIndex);
                if (expandedIndex !== -1) {
                    expandedClusters.splice(expandedIndex, 1);
                }

                // Remove individual markers
                var markersToRemove = allMarkers.filter(function(m) { return m._clusterIndex === clusterIndex; });
                markersToRemove.forEach(function(marker) {
                    map.removeLayer(marker);
                    allMarkers = allMarkers.filter(function(m) { return m !== marker; });
                });

                // Re-add cluster marker
                var clusterIcon = L.divIcon({
                    className: 'cluster-marker',
                    html: '<div style="background: #3b82f6; color: white; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 16px; border: 3px solid white; box-shadow: 0 4px 12px rgba(0,0,0,0.3); cursor: pointer; transition: transform 0.2s ease;">' + cluster.locations.length + '</div>',
                    iconSize: [40, 40],
                    iconAnchor: [20, 20]
                });

                var clusterMarker = L.marker([cluster.centerLat, cluster.centerLng], { icon: clusterIcon });
                clusterMarker._clusterId = clusterIndex;

                clusterMarker.on('click', function() {
                    expandCluster(clusterIndex);
                });

                allMarkers.push(clusterMarker);
                clusterMarker.addTo(map);
            }

            // Create markers or clusters
            clusters.forEach(function(cluster, clusterIndex) {
                if (cluster.locations.length === 1) {
                    // Single location - create normal marker
                    var marker = createIndividualMarker(cluster.locations[0]);
                    allMarkers.push(marker);
                    marker.addTo(map);
                } else {
                    // Multiple locations in same area - create cluster marker
                    var clusterIcon = L.divIcon({
                        className: 'cluster-marker',
                        html: '<div style="background: #3b82f6; color: white; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 16px; border: 3px solid white; box-shadow: 0 4px 12px rgba(0,0,0,0.3); cursor: pointer; transition: transform 0.2s ease;">' + cluster.locations.length + '</div>',
                        iconSize: [40, 40],
                        iconAnchor: [20, 20]
                    });

                    var clusterMarker = L.marker([cluster.centerLat, cluster.centerLng], { icon: clusterIcon });
                    clusterMarker._clusterId = clusterIndex;

                    // Click on cluster expands it
                    clusterMarker.on('click', function() {
                        expandCluster(clusterIndex);
                    });

                    allMarkers.push(clusterMarker);
                    clusterMarker.addTo(map);
                }
            });

            // Global function for viewing location details
            window.viewLocationDetails = function(locationId) {
                console.log('👁️ Opening details page for location:', locationId);

                // Generate the detail page URL
                var detailUrl = '/location/' + locationId;

                // Open in new tab/window
                if (window.parent && window.parent !== window) {
                    // If in iframe, send message to parent to open new tab
                    window.parent.postMessage({
                        type: 'openLocationDetail',
                        locationId: locationId,
                        url: detailUrl
                    }, '*');
                } else {
                    // If not in iframe, open directly
                    window.open(detailUrl, '_blank');
                }
            };

            // Add zoom event listener to auto-collapse clusters when zooming out
            map.on('zoomend', function() {
                var currentZoom = map.getZoom();
                if (currentZoom < 12) { // If zoomed out enough
                    // Collapse all expanded clusters
                    var expandedCopy = expandedClusters.slice(); // Copy array to avoid modification during iteration
                    expandedCopy.forEach(function(clusterIndex) {
                        collapseCluster(clusterIndex);
                    });
                }
            });

            // Fit bounds to show all markers
            if (allMarkers.length > 1) {
                var group = new L.featureGroup(allMarkers);
                map.fitBounds(group.getBounds().pad(0.1));
            } else if (allMarkers.length === 1) {
                map.setView(allMarkers[0].getLatLng(), 13);
            }

        } catch (error) {
            console.error('Map initialization error:', error);
            document.body.innerHTML = '<div style="padding: 20px; text-align: center; color: red;">❌ Erreur de chargement de la carte</div>';
        }
    </script>
</body>
</html>`;

    setMapHtml(`data:text/html,${encodeURIComponent(html)}`);
  }, [processedLocations, center]);

  // Handle iframe messages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === "locationSelect" && onLocationSelect) {
        const location = processedLocations.find((loc) => loc.id === event.data.locationId);
        if (location) {
          onLocationSelect(location);
        }
      } else if (event.data.type === "openLocationDetail") {
        // Open location detail page in new tab
        window.open(event.data.url, "_blank");
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [processedLocations, onLocationSelect]);

  if (!locations || locations.length === 0) {
    return (
      <motion.div
        className={`w-full bg-gray-100 rounded-lg flex items-center justify-center ${className}`}
        style={{ height: mapHeight }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-gray-500 text-center">
          <p className="text-lg mb-2">🗺️</p>
          <p>Aucune localisation à afficher</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className={`w-full rounded-lg overflow-hidden shadow-lg ${className}`}
      style={{ height: mapHeight }}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      {mapHtml ? (
        <iframe
          ref={iframeRef}
          src={mapHtml}
          width="100%"
          height="100%"
          style={{ border: "none" }}
          title="Carte interactive des services"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gray-100">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-gray-600">Chargement de la carte...</p>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default StableMap;
